import { useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import {
  EnrollmentReports,
  useActiveStudents,
} from '@/components/scheduled-lesson-report/StudentReports.hook';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import {
  scheduledLessonReportsQueryKeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import { createStudentReport } from '@/data/services/scheduledLessonReportServices';
import { updateScheduledLesson } from '@/data/services/scheduledLessonsServices';
import { EnrollmentStatusEnum } from '@/models/Enrollment';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import { PresenceEnum } from '@/models/ScheduledLessonReport';
import { handleMultipleRejectRequests } from '@/utils/erroHandlers';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { studentReportStatus } from '@/utils/studentReportStatus';
import { useConfirm } from '@/data/context/ConfirmModalContext';
import { useCallback } from 'react';
import joinReportAndEnrollment from '@/utils/scheduled-lesson-report/joinReportAndEnrollment';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';

type UseScheduledLessonSubmitBtnProps = {
  scheduledLesson: ScheduledLesson;
  klass: Klass;
  cacheManager?: ListCacheManager<ScheduledLesson>;
};

const handleError = (error: unknown) => alert.error(getErrorMessage(error));

export default function useScheduledLessonSubmitBtn({
  scheduledLesson,
  klass,
  cacheManager,
}: UseScheduledLessonSubmitBtnProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageScheduledLessonReport',
  });

  const { t: tConfirm } = useTranslation('translation', {
    keyPrefix: 'modalWarning.lessonReportModal',
  });

  const hasDone = scheduledLesson.hasDone;

  const { enrollments, reports } = useActiveStudents({
    active: true,
    klassId: klass.id,
    scheduledLesson,
  });

  const { queryKey } = scheduledLessonsQueryKeys.get(scheduledLesson.id);
  const cache = useSingleCache<ScheduledLesson>(queryKey);

  const queryClient = useQueryClient();

  const { isPending: isUpdating, mutateAsync: updateLessonReport } =
    useMutation({
      mutationFn: (hasDone: boolean) =>
        updateScheduledLesson(scheduledLesson.id, { hasDone }),
      onSuccess: async updatedScheduledLesson => {
        cache.set(updatedScheduledLesson);
        cacheManager?.update(updatedScheduledLesson);
        alert.success(t('savedScheduledLesson'));
        await queryClient.invalidateQueries({
          queryKey: scheduledLessonReportsQueryKeys.list._def,
        });
      },
    });

  const enrollmentReports = joinReportAndEnrollment({ enrollments, reports });

  const getActiveEnrollmentsReports = useCallback(
    (studentReports: EnrollmentReports[]) => {
      return studentReports.filter(
        ({ enrollment, report }) =>
          studentReportStatus({
            enrollmentStatus: enrollment.status,
            reportStatus: report?.status,
            wasUpdatedAfterLesson: moment(enrollment.updatedAt).isAfter(
              scheduledLesson.datetime,
            ),
          }) === EnrollmentStatusEnum.ACTIVE,
      );
    },
    [scheduledLesson.datetime],
  );

  const markAbsences = useCallback(
    async (studentReports: EnrollmentReports[]) => {
      const absenceRequests = studentReports
        .filter(
          ({ report }) =>
            !report?.presence || report.presence === PresenceEnum.MISSED,
        )
        .map(
          ({ enrollment, report }) =>
            !report &&
            createStudentReport({
              scheduledLesson: scheduledLesson.id,
              student: enrollment.student.id,
              presence: PresenceEnum.MISSED,
            }),
        );

      handleMultipleRejectRequests(await Promise.allSettled(absenceRequests));
    },
    [scheduledLesson.id],
  );

  const createPendingReports = useCallback(
    async (studentReports: EnrollmentReports[]) => {
      const createReportRequests = studentReports
        .filter(({ report }) => !report)
        .map(({ enrollment }) =>
          createStudentReport({
            student: enrollment.student.id,
            scheduledLesson: scheduledLesson.id,
          }),
        );

      await Promise.all(createReportRequests);
    },
    [scheduledLesson.id],
  );

  const setLessonAsDone = useCallback(async () => {
    await markAbsences(enrollmentReports);
    await updateLessonReport(true);
  }, [markAbsences, updateLessonReport, enrollmentReports]);

  const setLessonAsUndone = useCallback(async () => {
    const activeEnrollmentsReports =
      getActiveEnrollmentsReports(enrollmentReports);

    await createPendingReports(activeEnrollmentsReports);
    await updateLessonReport(false);
  }, [
    getActiveEnrollmentsReports,
    createPendingReports,
    updateLessonReport,
    enrollmentReports,
  ]);

  const confirm = useConfirm();

  function onSubmit() {
    if (hasDone) return setLessonAsUndone().catch(handleError);

    confirm({
      variant: 'default',
      title: tConfirm('attention'),
      description: tConfirm('proceed'),
      cancelLabel: tConfirm('cancel'),
      confirmLabel: tConfirm('confirm'),
      onConfirm: setLessonAsDone,
      onError: handleError,
    });
  }

  return { hasDone, isUpdating, onSubmit };
}
